import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Upload, Loader2 } from 'lucide-react';
import { uploadService } from '../services/uploadService';
import { useToast } from '../hooks/use-toast';

const ImageUpload = ({ maxFiles = 6, onUploadComplete, folder = 'mobility', existingImages = [] }) => {
  const [images, setImages] = useState(existingImages);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (images.length + acceptedFiles.length > maxFiles) {
      toast({
        title: 'Error',
        description: `Máximo ${maxFiles} imágenes permitidas`,
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      const urls = await uploadService.uploadMultipleImages(acceptedFiles, folder);
      const newImages = [...images, ...urls];
      setImages(newImages);
      onUploadComplete && onUploadComplete(newImages);
      toast({
        title: 'Éxito',
        description: `${acceptedFiles.length} imagen(es) subida(s)`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al subir imágenes',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  }, [images, maxFiles, onUploadComplete, folder, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: maxFiles - images.length,
    disabled: uploading || images.length >= maxFiles
  });

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onUploadComplete && onUploadComplete(newImages);
  };

  return (
    <div className="space-y-4">
      {/* Preview Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg"
              />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxFiles && (
        <Card
          {...getRootProps()}
          className={`border-2 border-dashed p-8 text-center cursor-pointer transition ${
            isDragActive ? 'border-[#000000] bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            {uploading ? (
              <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
            ) : (
              <Upload className="w-12 h-12 text-gray-400" />
            )}
            <p className="text-gray-600">
              {uploading
                ? 'Subiendo...'
                : isDragActive
                ? 'Suelta las imágenes aquí'
                : `Arrastra imágenes o haz clic para seleccionar (${images.length}/${maxFiles})`}
            </p>
            <p className="text-xs text-gray-500">PNG, JPG, WEBP hasta 10MB</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ImageUpload;
